/**
 * Adaptive Swiper for PDP complementary product cards.
 * Fills the row without empty slots; never shows more slides than products.
 */
if (!customElements.get('complementary-product-cards-swiper')) {
  class ComplementaryProductCardsSwiper extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.resizeObserver = null;
    }

    connectedCallback() {
      if (typeof Swiper === 'undefined') return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.initSwiper());
      });
      this.resizeObserver = new ResizeObserver(() => this.updateSlidesPerView());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    getSlideCount() {
      return this.querySelectorAll('.swiper-slide').length;
    }

    computeSlidesPerView() {
      const slideCount = this.getSlideCount();
      if (slideCount === 0) return 1;

      const minWidth = Number(this.dataset.minCardWidth) || 148;
      const gap = Number(this.dataset.spaceBetween) || 12;
      const maxVisible = Number(this.dataset.maxSlidesPerView) || 3;
      const width = this.clientWidth || this.offsetWidth;
      if (!width) return Math.min(slideCount, maxVisible);

      const maxByWidth = Math.max(1, Math.floor((width + gap) / (minWidth + gap)));
      return Math.min(slideCount, maxByWidth, maxVisible);
    }

    initSwiper() {
      if (this.swiper) return;

      const slideCount = this.getSlideCount();
      if (slideCount === 0) return;

      const gap = Number(this.dataset.spaceBetween) || 12;
      const slidesPerView = this.computeSlidesPerView();

      this.swiper = new Swiper(this, {
        slidesPerView,
        spaceBetween: gap,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: this.querySelector('.swiper-next'),
          prevEl: this.querySelector('.swiper-prev')
        },
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          clickable: true
        },
        on: {
          init: () => {
            this.classList.add('is-ready');
            this.toggleChrome();
          }
        }
      });

      if (this.swiper.initialized) {
        this.classList.add('is-ready');
      }

      this.toggleChrome();
    }

    updateSlidesPerView() {
      if (!this.swiper) return;

      const next = this.computeSlidesPerView();
      if (this.swiper.params.slidesPerView === next) {
        this.toggleChrome();
        return;
      }

      this.swiper.params.slidesPerView = next;
      this.swiper.update();
      this.toggleChrome();
    }

    toggleChrome() {
      if (!this.swiper) return;

      const hide = this.getSlideCount() <= this.swiper.params.slidesPerView;
      this.classList.toggle('is-static', hide);
    }

    refresh() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.classList.remove('is-ready', 'is-static');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.initSwiper());
      });
    }
  }

  customElements.define('complementary-product-cards-swiper', ComplementaryProductCardsSwiper);
}
